<template>
  <div>
    <v-layout>
      <v-flex>
        <v-stepper v-model="step" elevation="0" rounded flat>
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1">
              Paciente
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 2" step="2">
              Questionário
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 3" step="3">
              Sequência
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="step > 4" step="4">
              Terapia
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="5">Diagnóstico</v-stepper-step>
          </v-stepper-header>
          <v-stepper-content class="pa-0 mt-1" step="1">
            <form-patient
              v-if="step === 1"
              :is-saving-to-s3="isSavingToS3"
              :can-save-to-s3="draftInitialized"
              @next="next"
              @back="back"
              @save-to-s3="saveToS3Manually"
            ></form-patient>
          </v-stepper-content>
          <v-stepper-content class="pa-0 mt-1" step="2">
            <questions
              v-if="step === 2"
              :is-saving-to-s3="isSavingToS3"
              :can-save-to-s3="draftInitialized"
              @next="next"
              @back="back"
              @save-to-s3="saveToS3Manually"
            ></questions>
          </v-stepper-content>
          <v-stepper-content class="pa-0 mt-1" step="3">
            <sequences
              v-if="step === 3"
              :is-saving-to-s3="isSavingToS3"
              :can-save-to-s3="draftInitialized"
              @next="next"
              @back="back"
              @save-to-s3="saveToS3Manually"
            ></sequences>
          </v-stepper-content>
          <v-stepper-content class="pa-0 mt-1" step="4">
            <steps
              v-if="step === 4"
              :is-saving-to-s3="isSavingToS3"
              :can-save-to-s3="draftInitialized"
              @next="next"
              @back="back"
              @save-to-s3="saveToS3Manually"
            ></steps>
          </v-stepper-content>
          <v-stepper-content class="pa-0 mt-1" step="5">
            <diagnosis
              v-if="step === 5"
              :is-saving-to-s3="isSavingToS3"
              :can-save-to-s3="draftInitialized"
              @back="back"
              @save-to-s3="saveToS3Manually"
            ></diagnosis>
          </v-stepper-content>
        </v-stepper>
      </v-flex>
    </v-layout>
    <subscription-product-type-dialog />
  </div>
</template>

<script>
import db from "@/database";
import { debounce } from "lodash";
import { TherapyStatusEnum } from "@/domains/therapy/shared/application/therapy-status.enum";

export default {
  components: {
    "form-patient": () =>
      import(
        "@/domains/therapy/steps/patient/presentation/components/FormPatient"
      ),
    sequences: () =>
      import(
        "@/domains/therapy/steps/mount-sequence/presentation/components/Sequences"
      ),
    steps: () =>
      import("@/domains/therapy/steps/execute/presentation/components/Steps"),
    diagnosis: () =>
      import("@/domains/therapy/steps/diagnose/presentation/components/Main"),
    questions: () =>
      import(
        "@/domains/therapy/steps/questions/presentation/components/Questions"
      ),
    SubscriptionProductTypeDialog: () =>
      import(
        "@/domains/subscriptions/presentation/components/SubscriptionProductTypeDialog"
      ),
  },
  data() {
    return {
      autoSaveTimer: null,
      s3BackupTimer: null,
      lastBackupTime: null,
      backupInterval: 30000, // 30 segundos (fallback)
      minBackupInterval: 10000, // 10 segundos mínimo
      maxBackupInterval: 60000, // 60 segundos máximo
      draftInitialized: false,
      hasUnsavedChanges: false,
      lastActivityTime: null,
      therapyHash: null, // Hash para detectar mudanças reais
      isSavingToS3: false, // Estado de salvamento para animação do botão
    };
  },
  computed: {
    step: {
      get() {
        return this.$store.state.therapy.therapy.step;
      },
      set(value) {
        this.$store.dispatch("therapy/changeStep", value);
      },
    },
    therapy() {
      return this.$store.state.therapy.therapy;
    },
  },
  watch: {
    // Watch profundo em toda a terapia para auto-save local
    therapy: {
      handler: debounce(function (newVal, oldVal) {
        //console.log("[Auto-Save] Detectou mudança na terapia");
        this.hasUnsavedChanges = true;
        this.lastActivityTime = Date.now();
        this.autoSaveLocal();
      }, 2000), // Debounce de 2 segundos
      deep: true,
    },
    // Watch no step para criar draft quando avançar para Step 2 (após paciente)
    step: {
      handler(newStep, oldStep) {
        if (newStep === 2 && oldStep === 1 && !this.draftInitialized) {
          //console.log(
          //  "[Draft] Step 2 alcançado, criando draft na nuvem com dados do paciente"
          //);
          this.initDraftAndStartBackup();
        }

        // 🔥 OTIMIZAÇÃO: Backup imediato ao mudar de step (evento crítico)
        if (this.draftInitialized && newStep !== oldStep) {
          //console.log(
          //  "[S3 Backup] Mudança de step detectada, fazendo backup imediato"
          //);
          this.backupToS3();
        }
      },
    },
  },
  async mounted() {
    await this.syncOfflineData();

    // Se terapia carregada já tem draftId (recuperada da nuvem), inicia backup
    if (this.therapy.draftId && this.therapy.step >= 2) {
      //console.log(
      //  "[Draft] Terapia recuperada da nuvem detectada, iniciando backups"
      //);
      this.draftInitialized = true;
      this.startPeriodicS3Backup();
    }

    // 🚫 NÃO cria draft novo aqui - aguarda Step 2 (após paciente selecionado)
    // Isso evita criar drafts vazios na nuvem
  },
  beforeDestroy() {
    // Salva localmente antes de sair
    this.$store.dispatch("therapy/saveTherapyOffline");

    // Para timers
    this.stopPeriodicS3Backup();
  },
  methods: {
    next() {
      this.step++;
    },
    back() {
      this.step--;
    },
    async syncOfflineData() {
      await this.$store.dispatch("sync/verifyIfNeedsUpdateAndDownload");
    },

    /**
     * Inicia draft no backend E começa backups periódicos (1x apenas, após paciente selecionado)
     */
    async initDraftAndStartBackup() {
      try {
        // Só cria se não existir draftId
        if (!this.therapy.draftId) {
          await this.$store.dispatch("therapy/initTherapyDraft");
          this.draftInitialized = true;
          //console.log(
          //  "[Draft] Draft inicializado com sucesso (com dados do paciente)"
          //);

          // Faz primeiro backup imediatamente
          await this.backupToS3();

          // Inicia backups periódicos
          this.startPeriodicS3Backup();
        } else {
          this.draftInitialized = true;
          //console.log("[Draft] Draft já existente, reutilizando");
          this.startPeriodicS3Backup();
        }
      } catch (error) {
        console.error("[Draft] Erro ao inicializar draft:", error);
        this.$toasted.error(
          "Erro ao iniciar rascunho na nuvem. Continue normalmente, dados salvos localmente."
        );
        // Continua sem draft (apenas offline-first)
      }
    },

    /**
     * Auto-save local (IndexedDB)
     */
    async autoSaveLocal() {
      try {
        await this.$store.dispatch("therapy/saveTherapyOffline");
        //console.log("[Auto-Save] Terapia salva no IndexedDB");
      } catch (error) {
        console.error("[Auto-Save] Erro ao salvar:", error);
      }
    },

    /**
     * Inicia backup periódico no S3 - ADAPTATIVO
     * - Intervalo dinâmico baseado em atividade do usuário
     * - Apenas faz backup se houver mudanças não salvas
     */
    startPeriodicS3Backup() {
      // Só inicia se draft já foi criado
      if (!this.draftInitialized) {
        //console.log("[S3 Backup] Draft não inicializado, não iniciando timer");
        return;
      }

      // Só faz backup se status é IN_PROGRESS
      if (this.therapy.status === TherapyStatusEnum.IN_PROGRESS) {
        this.s3BackupTimer = setInterval(() => {
          this.intelligentBackup();
        }, this.minBackupInterval); // Verifica a cada 10s, mas só faz backup se necessário

        console.log(
          "[S3 Backup] Timer adaptativo iniciado (verifica a cada 10s)"
        );
      } else {
        console.log(
          "[S3 Backup] Status não é IN_PROGRESS, não iniciando timer. Status atual:",
          this.therapy.status
        );
      }
    },

    /**
     * Backup inteligente - decide se deve fazer backup baseado em:
     * - Tempo desde último backup
     * - Se há mudanças não salvas
     * - Atividade do usuário
     */
    async intelligentBackup() {
      const now = Date.now();
      const timeSinceLastBackup = this.lastBackupTime
        ? now - this.lastBackupTime
        : Infinity;
      const timeSinceActivity = this.lastActivityTime
        ? now - this.lastActivityTime
        : Infinity;

      // 🔥 REGRA 1: Se há mudanças não salvas E passou tempo mínimo (10s)
      if (
        this.hasUnsavedChanges &&
        timeSinceLastBackup >= this.minBackupInterval
      ) {
        //console.log("[S3 Backup] Mudanças detectadas, fazendo backup");
        await this.backupToS3();
        return;
      }

      // 🔥 REGRA 2: Se passou muito tempo sem backup (60s), faz backup de segurança
      if (timeSinceLastBackup >= this.maxBackupInterval) {
        //console.log("[S3 Backup] Backup de segurança (60s sem backup)");
        await this.backupToS3();
        return;
      }

      // 🔥 REGRA 3: Usuário inativo por 15s E há mudanças não salvas
      if (
        this.hasUnsavedChanges &&
        timeSinceActivity >= 15000 &&
        timeSinceLastBackup >= this.minBackupInterval
      ) {
        //console.log("[S3 Backup] Usuário inativo, fazendo backup");
        await this.backupToS3();
        return;
      }

      // Caso contrário, aguarda próxima verificação
      //console.log(
      //  "[S3 Backup] Sem necessidade de backup no momento (última atividade:",
      //  Math.round(timeSinceActivity / 1000),
      //  "s atrás)"
      //);
    },

    /**
     * Para backup periódico
     */
    stopPeriodicS3Backup() {
      if (this.s3BackupTimer) {
        clearInterval(this.s3BackupTimer);
        //console.log("[S3 Backup] Timer parado");
      }
    },

    /**
     * Envia backup para S3
     */
    async backupToS3() {
      try {
        this.isSavingToS3 = true;
        await this.$store.dispatch("therapy/backupTherapyToS3");

        this.lastBackupTime = Date.now();
        this.hasUnsavedChanges = false; // Marca que foi salvo

        // Notifica apenas ocasionalmente (não em todo backup)
        if (!this.lastBackupTime || Date.now() - this.lastBackupTime > 60000) {
          this.$toasted.info("Rascunho salvo na nuvem", {
            duration: 2000,
          });
        }
      } catch (error) {
        console.error("[S3 Backup] Erro ao fazer backup:", error);
        // Não exibe erro ao usuário (offline-first continua funcionando)
      } finally {
        this.isSavingToS3 = false;
      }
    },

    /**
     * Salvamento manual acionado pelo botão
     */
    async saveToS3Manually() {
      if (!this.draftInitialized) {
        this.$toasted.warning(
          "Selecione um paciente antes de salvar na nuvem",
          {
            duration: 3000,
          }
        );
        return;
      }

      try {
        this.isSavingToS3 = true;
        await this.$store.dispatch("therapy/backupTherapyToS3");

        this.lastBackupTime = Date.now();
        this.hasUnsavedChanges = false;

        this.$toasted.success("Terapia salva na nuvem com sucesso!", {
          duration: 3000,
        });
      } catch (error) {
        console.error("[S3 Backup Manual] Erro ao fazer backup:", error);
        this.$toasted.error("Erro ao salvar na nuvem. Tente novamente.", {
          duration: 3000,
        });
      } finally {
        this.isSavingToS3 = false;
      }
    },
  },
};
</script>

<style scoped></style>
